/**
 * Word-level diff, no dependency.
 *
 * The before/after comparison is the single most important comprehension task in this
 * product — a reviewer is deciding whether a machine may alter a document going to a funder.
 * Today both /new and /review render the two versions as two plain paragraphs, which asks
 * the reviewer to spot the change by eye. This produces the token stream a real diff needs.
 *
 * Segments are emitted in reading order so unchanged text still reads as a sentence and the
 * eye lands only on what actually moved. Built on a standard LCS over word tokens.
 */

export type DiffSegment = {
  type: "equal" | "insert" | "delete";
  value: string;
};

/** Split into words while keeping the whitespace attached, so joins are lossless. */
function tokenize(text: string): string[] {
  return text.match(/\S+\s*/g) || [];
}

/** Longest common subsequence table over two token arrays. */
function lcsTable(a: string[], b: string[]): number[][] {
  const table: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i][j] = a[i].trim() === b[j].trim()
        ? table[i + 1][j + 1] + 1
        : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  return table;
}

/**
 * Diff `before` against `after` at word granularity.
 * Adjacent segments of the same type are merged so the DOM stays small.
 */
export function diffWords(before: string, after: string): DiffSegment[] {
  const a = tokenize(before);
  const b = tokenize(after);
  const table = lcsTable(a, b);
  const segments: DiffSegment[] = [];

  const push = (type: DiffSegment["type"], value: string) => {
    const last = segments[segments.length - 1];
    if (last && last.type === type) last.value += value;
    else segments.push({ type, value });
  };

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i].trim() === b[j].trim()) {
      push("equal", a[i]);
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      push("delete", a[i]);
      i += 1;
    } else {
      push("insert", b[j]);
      j += 1;
    }
  }
  while (i < a.length) {
    push("delete", a[i]);
    i += 1;
  }
  while (j < b.length) {
    push("insert", b[j]);
    j += 1;
  }

  return segments;
}

/**
 * Protected content: numbers, dates, currencies, citations, URLs.
 *
 * The product's core promise is that the machine never silently alters these. A reviewer
 * must be able to SEE that promise being kept, so the diff renders each match as a sealed
 * chip. Detection is deliberately broad — over-marking a token as protected is safe;
 * under-marking it is not.
 */
const PROTECTED_PATTERN = new RegExp(
  [
    "https?://[^\\s]+", // urls
    "[A-Z]{2,}\\$[\\d,.]+(?:[KMB])?(?:-\\$?[\\d,.]+(?:[KMB])?)?", // US$6.5M-$25M
    "[$£€]\\s?[\\d,.]+(?:[KMB])?", // currencies
    "\\b\\d{4}-\\d{2}-\\d{2}\\b", // iso dates
    "\\b\\d+(?:\\.\\d+)?%", // percentages
    "\\b\\d+(?:\\.\\d+)?\\s?(?:months?|years?|weeks?|days?)\\b", // durations
    "\\[\\d+\\]", // citation markers
    "\\b\\d[\\d,.]*\\b" // bare numbers, last so the richer patterns win
  ].join("|"),
  "gi"
);

export type ProtectedSegment = {
  protected: boolean;
  value: string;
};

/**
 * THE HONEST DELTA. What a collapsed change card may state about an edit WITHOUT being
 * expanded: how many words leave, how many arrive, and how many protected facts survive
 * the edit untouched.
 *
 * Every number here is COUNTED from the same two functions the diff itself renders from —
 * diffWords() for the word counts, markProtected() for the sealed tokens — so the summary
 * line and the diff below it can never disagree. Nothing is estimated, scored, or rounded.
 *
 * `protectedKept` counts DISTINCT protected tokens present in BOTH sides. "Kept" is a claim
 * about survival, so a token that only exists in `after` (a number the machine introduced)
 * is deliberately NOT counted as kept — it was not there to keep.
 */
export type DiffStats = {
  removed: number;
  added: number;
  protectedKept: number;
};

function protectedTokens(text: string): Set<string> {
  const out = new Set<string>();
  for (const piece of markProtected(text)) {
    if (piece.protected) out.add(piece.value.trim());
  }
  return out;
}

function countWords(value: string) {
  return (value.match(/\S+/g) || []).length;
}

export function diffStats(before: string, after: string): DiffStats {
  let removed = 0;
  let added = 0;
  for (const segment of diffWords(before, after)) {
    if (segment.type === "delete") removed += countWords(segment.value);
    else if (segment.type === "insert") added += countWords(segment.value);
  }

  const beforeProtected = protectedTokens(before);
  const afterProtected = protectedTokens(after);
  let protectedKept = 0;
  for (const token of beforeProtected) {
    if (afterProtected.has(token)) protectedKept += 1;
  }

  return { removed, added, protectedKept };
}

/**
 * THE FACT-AWARE MARK. markProtected() is deliberately broad — it tags every number-shaped run it
 * sees — and that is correct for a DETECTOR whose failure mode must be over-marking. It is NOT
 * correct for a RENDERER: a sealed amber chip is an ASSERTION that Final QA checks this string
 * against the canonical source, and Final QA checks the DECLARED protected facts.
 *
 * THE DEFECT THIS FIXES. The detector ran FIRST and a declared fact was only ever used to
 * whitelist the fragments it happened to contain, so one screen asserted two different
 * definitions of a protected fact at once: the chip row printed the whole fact
 * "US$6.5M-$25M over 36 months", while the prose one line below it printed TWO seals —
 * [US$6.5M-$25M] and [36 months] — with a naked "over" stranded between them, and boxed the "39"
 * inside "39-page" as though a compound adjective's first syllable were a governed figure.
 *
 * SO THE DECLARED FACTS ARE MATCHED FIRST, longest-first, on word boundaries, and they CLAIM
 * their span of the text. Only the text no declared fact has claimed is offered to the detector,
 * and a detected run is sealed only when it is RICH (a currency, a date, a duration, a
 * percentage, a citation, a URL, a figure of three digits or more) — a bare one- or two-digit run
 * names nothing on its own. No seal may ever begin or end INSIDE a word or a hyphenated compound.
 *
 * The renderer can therefore only ever seal what it can stand behind, and every surface on the
 * editor — the chip row, the prose, the locked block, both panes of the diff — renders from this
 * one function, so they cannot disagree. Final QA still compares every declared fact against the
 * canonical source regardless of what any surface displays.
 */

/** A claimed span of the text. Spans never overlap: the first claimant wins. */
type ProtectedSpan = { start: number; end: number };

const WORD_CHAR = /[\p{L}\p{N}_]/u;

/**
 * A seal may not cut a word in half. A span is only sealable when neither edge lands inside a
 * word ("...the 39|-page..." is the first syllable of a compound, not a governed figure) and
 * neither edge is bound to one by a hyphen.
 */
function onWordBoundary(text: string, start: number, end: number) {
  const before = text[start - 1];
  const after = text[end];
  if (before && (WORD_CHAR.test(before) || (before === "-" && WORD_CHAR.test(text[start - 2] || "")))) return false;
  if (after && (WORD_CHAR.test(after) || (after === "-" && WORD_CHAR.test(text[end + 1] || "")))) return false;
  return true;
}

export function markProtectedFacts(text: string, facts: string[] = []): ProtectedSegment[] {
  const spans: ProtectedSpan[] = [];
  const overlaps = (start: number, end: number) =>
    spans.some((span) => start < span.end && end > span.start);

  const claim = (start: number, end: number) => {
    if (end <= start) return;
    if (overlaps(start, end)) return;
    if (!onWordBoundary(text, start, end)) return;
    spans.push({ start, end });
  };

  /* 1. THE DECLARED FACTS, LONGEST FIRST. "US$6.5M-$25M over 36 months" claims its whole span
        before the detector can see the "$25M" and the "36 months" inside it as two facts. */
  const declared = Array.from(new Set(facts.filter(Boolean))).sort((a, b) => b.length - a.length);
  for (const fact of declared) {
    let index = text.indexOf(fact);
    while (index !== -1) {
      claim(index, index + fact.length);
      index = text.indexOf(fact, index + 1);
    }
  }

  /* 2. THE DETECTOR, on what is left. Rich tokens only — a bare "9" or "39" is a fragment. */
  const pattern = new RegExp(PROTECTED_PATTERN.source, PROTECTED_PATTERN.flags);
  let match = pattern.exec(text);
  while (match) {
    const value = match[0];
    const bareFragment = /^\d{1,2}$/.test(value.trim());
    if (!bareFragment) claim(match.index, match.index + value.length);
    match = pattern.exec(text);
  }

  /* 3. EMIT IN READING ORDER. Protected runs are never merged with each other: two adjacent
        facts are two facts, and one chip around both would name a string that is neither. */
  spans.sort((a, b) => a.start - b.start);
  const out: ProtectedSegment[] = [];
  const pushPlain = (value: string) => {
    if (!value) return;
    const last = out[out.length - 1];
    if (last && !last.protected) last.value += value;
    else out.push({ protected: false, value });
  };

  let cursor = 0;
  for (const span of spans) {
    pushPlain(text.slice(cursor, span.start));
    out.push({ protected: true, value: text.slice(span.start, span.end) });
    cursor = span.end;
  }
  pushPlain(text.slice(cursor));

  return out;
}

/** Split a string into protected / unprotected runs, in order. */
export function markProtected(text: string): ProtectedSegment[] {
  const out: ProtectedSegment[] = [];
  let lastIndex = 0;
  // A fresh regex per call: /g regexes carry lastIndex state across calls.
  const pattern = new RegExp(PROTECTED_PATTERN.source, PROTECTED_PATTERN.flags);
  let match = pattern.exec(text);
  while (match) {
    if (match.index > lastIndex) out.push({ protected: false, value: text.slice(lastIndex, match.index) });
    out.push({ protected: true, value: match[0] });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (lastIndex < text.length) out.push({ protected: false, value: text.slice(lastIndex) });
  return out;
}
